import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Plus, Trash, PencilSimple, X, Kanban, CalendarBlank, CaretLeft, CaretRight, Microphone, Star } from "@phosphor-icons/react";

const COLUMNS = [
  { key: "applied", label: "Applied", bg: "bg-[var(--pastel-yellow)]" },
  { key: "interview", label: "Interview", bg: "bg-[var(--pastel-mint)]" },
  { key: "offer", label: "Offer", bg: "bg-[var(--pastel-peach)]" },
  { key: "rejected", label: "Rejected", bg: "bg-white" },
];

const EMPTY_FORM = {
  company: "", role: "", status: "applied", notes: "", job_url: "",
  applied_date: "", closing_date: "", interview_date: "", offer_date: "", salary: "",
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function isoDate(d) { return d.toISOString().slice(0, 10); }

export default function Tracker() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [view, setView] = useState("kanban"); // kanban | calendar
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  // status-change prompt state
  const [statusPrompt, setStatusPrompt] = useState(null); // { item, newStatus }
  const [promptDate, setPromptDate] = useState("");
  // Post-mortem panel state
  const [pmFor, setPmFor] = useState(null); // application object
  const [pmList, setPmList] = useState([]);
  const [pmForm, setPmForm] = useState(null); // form state when adding/editing
  const EMPTY_PM = {
    stage: "Recruiter screen", rating: 3, interviewer_vibe: 3, process_speed: 3,
    went_well: "", went_poorly: "", lessons: "", would_recommend_company: true, interview_date: "",
  };

  const load = async () => {
    const { data } = await api.get("/applications");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Demo-data detection — any application whose notes begin with "demo ·" was
  // inserted by /app/scripts/seed_demo_tracker.py so users see the calendar
  // populated on first login.
  const isDemo = (app) => (app?.notes || "").trim().toLowerCase().startsWith("demo ·");
  const demoApps = items.filter(isDemo);

  const clearDemoData = async () => {
    if (!demoApps.length) return;
    if (!confirm(`Delete all ${demoApps.length} demo entries? This can't be undone.`)) return;
    try {
      await Promise.all(demoApps.map((a) => api.delete(`/applications/${a.id}`)));
      toast.success(`removed ${demoApps.length} demo entries — over to you ✨`);
      load();
    } catch {
      toast.error("couldn't clear demo data — try again");
    }
  };

  const openNew = () => { setEditing("new"); setForm(EMPTY_FORM); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...EMPTY_FORM, ...it }); };
  const cancel = () => { setEditing(null); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/applications", form);
      else await api.put(`/applications/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Couldn't save");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/applications/${id}`);
    load();
  };

  // Move-to with optional date capture (interview/offer prompt user)
  const doMove = async (it, newStatus, extraDate = null) => {
    setItems((prev) => prev.map((i) => (i.id === it.id ? { ...i, status: newStatus, ...(extraDate || {}) } : i)));
    try {
      await api.put(`/applications/${it.id}`, { ...it, status: newStatus, ...(extraDate || {}) });
    } catch {
      toast.error("Move failed");
      load();
    }
  };

  const moveTo = (it, status) => {
    if (it.status === status) return;
    if (status === "interview" && !it.interview_date) {
      setStatusPrompt({ item: it, newStatus: status, field: "interview_date", label: "Interview date" });
      setPromptDate("");
      return;
    }
    if (status === "offer" && !it.offer_date) {
      setStatusPrompt({ item: it, newStatus: status, field: "offer_date", label: "Offer date" });
      setPromptDate("");
      return;
    }
    doMove(it, status);
  };

  const confirmStatusPrompt = async () => {
    if (!statusPrompt) return;
    const { item, newStatus, field } = statusPrompt;
    const extra = promptDate ? { [field]: promptDate } : {};
    await doMove(item, newStatus, extra);
    toast.success(promptDate ? `Moved · ${statusPrompt.label} saved` : "Moved");
    setStatusPrompt(null);
    setPromptDate("");
  };
  const skipStatusPrompt = async () => {
    if (!statusPrompt) return;
    const { item, newStatus } = statusPrompt;
    await doMove(item, newStatus);
    setStatusPrompt(null);
  };

  // Drag-drop
  const onDragStart = (e, it) => {
    setDragId(it.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", it.id);
  };
  const onDragOver = (e, col) => { e.preventDefault(); setDragOver(col); };
  const onDrop = (e, col) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    const it = items.find((i) => i.id === id);
    if (it) moveTo(it, col);
    setDragId(null);
    setDragOver(null);
  };

  // ---- Post-mortems ----
  const openPmPanel = async (app) => {
    setPmFor(app);
    setPmForm(null);
    try {
      const { data } = await api.get(`/applications/${app.id}/postmortems`);
      setPmList(data);
    } catch {
      setPmList([]);
    }
  };
  const closePmPanel = () => { setPmFor(null); setPmForm(null); setPmList([]); };
  const savePm = async () => {
    if (!pmFor || !pmForm) return;
    try {
      if (pmForm.id) {
        await api.put(`/applications/${pmFor.id}/postmortems/${pmForm.id}`, pmForm);
      } else {
        await api.post(`/applications/${pmFor.id}/postmortems`, pmForm);
      }
      toast.success("Post-mortem saved");
      const { data } = await api.get(`/applications/${pmFor.id}/postmortems`);
      setPmList(data);
      setPmForm(null);
    } catch {
      toast.error("Couldn't save");
    }
  };
  const deletePm = async (pmId) => {
    if (!pmFor) return;
    if (!confirm("Delete this post-mortem?")) return;
    await api.delete(`/applications/${pmFor.id}/postmortems/${pmId}`);
    const { data } = await api.get(`/applications/${pmFor.id}/postmortems`);
    setPmList(data);
  };

  // ---- Calendar view: bucket events per date ----
  const calendarEvents = useMemo(() => {
    const events = {};
    const push = (date, ev) => {
      if (!date) return;
      const key = date.slice(0, 10);
      if (!events[key]) events[key] = [];
      events[key].push(ev);
    };
    items.forEach((it) => {
      if (it.closing_date) push(it.closing_date, { ...it, _kind: "closing" });
      if (it.interview_date) push(it.interview_date, { ...it, _kind: "interview" });
      if (it.offer_date) push(it.offer_date, { ...it, _kind: "offer" });
    });
    return events;
  }, [items]);

  const monthGrid = useMemo(() => {
    const first = startOfMonth(calendarMonth);
    const firstDow = (first.getDay() + 6) % 7; // 0=Mon
    const days = daysInMonth(calendarMonth);
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarMonth]);

  const KIND_CLASS = {
    closing: "is-deadline",
    interview: "is-interview",
    offer: "is-offer",
  };
  const KIND_LABEL = { closing: "deadline", interview: "interview", offer: "offer" };

  return (
    <AppShell title="Application Tracker">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-[var(--brand-muted)] max-w-2xl">
          applied → interview → offer → rejected → therapy. drag cards or use the calendar.
        </p>
        <div className="flex items-center gap-2">
          <div className="card-hard rounded-full p-1 flex" data-testid="view-toggle">
            <button onClick={() => setView("kanban")}
              data-testid="view-kanban"
              className={`pill-tab ${view === "kanban" ? "active" : ""}`}>
              <Kanban size={14}/> kanban
            </button>
            <button onClick={() => setView("calendar")}
              data-testid="view-calendar"
              className={`pill-tab ${view === "calendar" ? "active" : ""}`}>
              <CalendarBlank size={14}/> calendar
            </button>
          </div>
          <button onClick={openNew} data-testid="tracker-add"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 flex items-center gap-2">
            <Plus size={16} weight="bold"/> log application
          </button>
        </div>
      </div>

      {/* Demo-data banner — clears confusion about Lighthouse Studio / Northwind Health etc. */}
      {demoApps.length > 0 && (
        <div className="card-hard rounded-md p-4 bg-[var(--bolt-butter)] mb-5 flex items-start gap-3 flex-wrap"
          data-testid="demo-banner">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-white font-mono-d text-[10px] tracking-[0.18em] uppercase mt-0.5">
            heads up
          </span>
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm font-medium">
              {demoApps.length} example {demoApps.length === 1 ? "entry" : "entries"} are pre-loaded so the tracker
              isn&apos;t empty on day one — they&apos;re labelled <b>example</b>.
            </p>
            <p className="text-xs text-[var(--brand-ink)]/70 mt-1">
              none of these jobs are real. delete them whenever you&apos;re ready to add your own.
            </p>
          </div>
          <button onClick={clearDemoData} data-testid="clear-demo-data"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-1.5 text-xs">
            clear example data
          </button>
        </div>
      )}

      {/* Status-change prompt */}
      {statusPrompt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="status-prompt">
          <div className="card-hard rounded-md p-6 bg-[var(--brand-bg)] max-w-md w-full">
            <p className="label-tiny mb-2">/{statusPrompt.newStatus}</p>
            <h3 className="font-display text-2xl font-semibold tracking-tight mb-2">
              {statusPrompt.label}?
            </h3>
            <p className="text-sm text-[var(--brand-muted)] mb-4">
              quick win — drop the date so it lands on your calendar. you can skip.
            </p>
            <input type="date" value={promptDate} onChange={(e) => setPromptDate(e.target.value)}
              data-testid="status-prompt-date"
              className="w-full card-hard rounded-md px-3 py-2 bg-white mb-4"/>
            <div className="flex gap-2 justify-end">
              <button onClick={skipStatusPrompt} data-testid="status-prompt-skip"
                className="btn-hard bg-white rounded-md px-3 py-2 text-sm">skip</button>
              <button onClick={confirmStatusPrompt} data-testid="status-prompt-save"
                className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-3 py-2 text-sm">save</button>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-6" data-testid="tracker-editor">
          <div className="flex items-center justify-between mb-4">
            <p className="label-tiny">/{editing === "new" ? "new application" : "edit"}</p>
            <button onClick={cancel}><X size={18}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-tiny block mb-1.5">Company</label>
              <input data-testid="app-field-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Role</label>
              <input data-testid="app-field-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Status</label>
              <select data-testid="app-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white">
                {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Applied date</label>
              <input type="date" data-testid="app-field-date" value={form.applied_date || ""} onChange={(e) => setForm({ ...form, applied_date: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Closing date (deadline)</label>
              <input type="date" data-testid="app-field-closing" value={form.closing_date || ""} onChange={(e) => setForm({ ...form, closing_date: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Interview date</label>
              <input type="date" data-testid="app-field-interview" value={form.interview_date || ""} onChange={(e) => setForm({ ...form, interview_date: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Offer date</label>
              <input type="date" data-testid="app-field-offer-date" value={form.offer_date || ""} onChange={(e) => setForm({ ...form, offer_date: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Salary</label>
              <input data-testid="app-field-salary" value={form.salary || ""} onChange={(e) => setForm({ ...form, salary: e.target.value })}
                placeholder="e.g. £32k"
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div className="md:col-span-2">
              <label className="label-tiny block mb-1.5">Job URL</label>
              <input data-testid="app-field-url" value={form.job_url || ""} onChange={(e) => setForm({ ...form, job_url: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div className="md:col-span-2">
              <label className="label-tiny block mb-1.5">Notes</label>
              <textarea data-testid="app-field-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 min-h-[80px] bg-white"/>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} data-testid="app-save"
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm">save</button>
            <button onClick={cancel} className="btn-hard bg-white rounded-md px-4 py-2 text-sm">cancel</button>
          </div>
        </div>
      )}

      {/* Kanban */}
      {view === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban">
          {COLUMNS.map((col) => {
            const colItems = items.filter((i) => i.status === col.key);
            const isOver = dragOver === col.key;
            return (
              <div key={col.key}
                onDragOver={(e) => onDragOver(e, col.key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, col.key)}
                className={`card-hard rounded-md ${col.bg} ${isOver ? "ring-4 ring-[var(--brand-primary)]" : ""}`}
                data-testid={`column-${col.key}`}>
                <div className="p-4 border-b-[1.5px] border-[var(--brand-ink)] flex items-center justify-between">
                  <p className="label-tiny">{col.label}</p>
                  <span className="font-mono-d text-xs">{colItems.length}</span>
                </div>
                <div className="p-3 space-y-3 min-h-[140px]">
                  {colItems.length === 0 && (
                    <p className="text-xs font-mono-d text-[var(--brand-muted)] text-center py-6">drop cards here</p>
                  )}
                  {colItems.map((it) => (
                    <div key={it.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, it)}
                      className={`border-[1.5px] border-[var(--brand-ink)] rounded-md p-3 bg-white cursor-move relative ${dragId === it.id ? "opacity-50" : ""} ${isDemo(it) ? "border-dashed" : ""}`}
                      data-testid={`app-card-${it.id}`}>
                      {isDemo(it) && (
                        <span data-testid={`demo-tag-${it.id}`}
                          className="absolute -top-2 -right-2 inline-flex items-center px-2 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-[var(--bolt-mustard)] font-mono-d text-[9px] tracking-[0.15em] uppercase shadow-[2px_2px_0_var(--brand-ink)]">
                          example
                        </span>
                      )}
                      <p className="font-medium text-sm">{it.role}</p>
                      <p className="text-xs text-[var(--brand-muted)] mt-0.5">{it.company}</p>
                      {it.applied_date && <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">applied · {it.applied_date}</p>}
                      {it.closing_date && <p className="text-xs font-mono-d text-[var(--brand-muted)]">closes · {it.closing_date}</p>}
                      {it.interview_date && <p className="text-xs font-mono-d text-[var(--brand-muted)]">interview · {it.interview_date}</p>}
                      <div className="mt-2 flex flex-wrap gap-1">
                        <select value={it.status} onChange={(e) => moveTo(it, e.target.value)}
                          data-testid={`status-${it.id}`}
                          className="text-xs border-[1.5px] border-[var(--brand-ink)] rounded px-1 py-0.5 bg-white">
                          {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                        <button onClick={() => openPmPanel(it)} className="p-1 hover:bg-[var(--pastel-mint)] rounded"
                          title="Post-mortem"
                          data-testid={`pm-${it.id}`}><Microphone size={14}/></button>
                        <button onClick={() => openEdit(it)} className="p-1 hover:bg-[var(--pastel-yellow)] rounded"
                          data-testid={`edit-app-${it.id}`}><PencilSimple size={14}/></button>
                        <button onClick={() => remove(it.id)} className="p-1 hover:bg-[var(--pastel-peach)] rounded"
                          data-testid={`delete-app-${it.id}`}><Trash size={14}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Calendar */}
      {view === "calendar" && (
        <div className="card-hard rounded-md bg-white overflow-hidden" data-testid="calendar-view">
          <div className="h-1.5 w-full" style={{ background: "var(--bolt-terracotta)" }} aria-hidden="true"/>
          <div className="p-4 border-b-[1.5px] border-[var(--brand-ink)] flex items-center justify-between flex-wrap gap-3 bg-[var(--bolt-cream)]">
            <div className="flex items-center gap-2">
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                data-testid="cal-prev" className="btn-hard bg-white rounded-md p-1.5"><CaretLeft size={16}/></button>
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                data-testid="cal-next" className="btn-hard bg-white rounded-md p-1.5"><CaretRight size={16}/></button>
              <button onClick={() => setCalendarMonth(startOfMonth(new Date()))}
                data-testid="cal-today" className="btn-hard bg-[var(--bolt-mustard)] rounded-md px-3 py-1.5 text-xs font-medium">today</button>
              <h2 className="font-display text-2xl font-semibold tracking-tight ml-2" data-testid="cal-month">
                {MONTHS[calendarMonth.getMonth()]} <span className="text-[var(--bolt-terracotta)]">{calendarMonth.getFullYear()}</span>
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="legend-dot"><span className="swatch" style={{background:"#FFCFC0"}}/>deadline</span>
              <span className="legend-dot"><span className="swatch" style={{background:"#C7EBD7"}}/>interview</span>
              <span className="legend-dot"><span className="swatch" style={{background:"var(--bolt-butter)"}}/>offer</span>
            </div>
          </div>
          <div className="grid grid-cols-7 border-b-[1.5px] border-[var(--brand-ink)] bg-[var(--brand-bg)]">
            {DAYS.map((d, i) => (
              <div key={d} className={`cal-dow ${i >= 5 ? "is-weekend" : ""}`}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid.map((d, idx) => {
              const key = d ? isoDate(d) : `e-${idx}`;
              const events = d ? (calendarEvents[isoDate(d)] || []) : [];
              const isToday = d && isoDate(d) === isoDate(new Date());
              const dow = d ? (d.getDay() + 6) % 7 : 0;
              const isWeekend = d && dow >= 5;
              const cls = !d
                ? "cal-cell is-empty"
                : `cal-cell ${isToday ? "is-today" : ""} ${isWeekend && !isToday ? "is-weekend" : ""}`;
              return (
                <div key={key}
                  data-testid={d ? `cal-day-${isoDate(d)}` : undefined}
                  className={cls}>
                  {d && (
                    <>
                      <span className="cal-daynum" data-testid={isToday ? "cal-today-num" : undefined}>{d.getDate()}</span>
                      <div className="mt-2 space-y-1.5">
                        {events.slice(0, 3).map((ev, i) => (
                          <div key={i}
                            onClick={() => openEdit(ev)}
                            data-testid={`cal-event-${ev.id}-${ev._kind}`}
                            className={`event-pill ${KIND_CLASS[ev._kind] || ""} ${isDemo(ev) ? "is-demo" : ""}`}
                            title={`${isDemo(ev) ? "EXAMPLE · " : ""}${KIND_LABEL[ev._kind]} · ${ev.company} · ${ev.role}`}>
                            <b className="uppercase tracking-wider" style={{fontSize:"9px"}}>
                              {isDemo(ev) ? "ex · " : ""}{KIND_LABEL[ev._kind]}
                            </b>
                            <span className="ml-1">{ev.company || ev.role}</span>
                          </div>
                        ))}
                        {events.length > 3 && (
                          <p className="text-[10px] font-mono-d text-[var(--brand-muted)] pl-1">+{events.length - 3} more</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Post-mortem side drawer */}
      {pmFor && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={closePmPanel}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[var(--brand-bg)] h-full overflow-y-auto p-6 border-l-[1.5px] border-[var(--brand-ink)]"
            data-testid="pm-drawer">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-tiny">/post-mortem</p>
                <h2 className="font-display text-2xl tracking-tight">{pmFor.role}</h2>
                <p className="text-sm text-[var(--brand-muted)]">{pmFor.company}</p>
              </div>
              <button onClick={closePmPanel} data-testid="pm-close" className="btn-hard bg-white rounded-md p-1.5"><X size={18}/></button>
            </div>

            {pmList.length > 0 && (
              <div className="space-y-3 mb-4" data-testid="pm-list">
                {pmList.map((pm) => (
                  <div key={pm.id} className="card-hard rounded-md p-4 bg-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{pm.stage}</p>
                        {pm.interview_date && <p className="font-mono-d text-xs text-[var(--brand-muted)]">{pm.interview_date}</p>}
                      </div>
                      <span className="inline-flex items-center gap-0.5 font-mono-d text-xs">
                        {[1,2,3,4,5].map((i) => <Star key={i} size={12} weight={i <= (pm.rating || 0) ? "fill" : "regular"} className={i <= pm.rating ? "text-[var(--brand-primary)]" : "text-[var(--brand-muted)]"}/>)}
                      </span>
                    </div>
                    {pm.went_well && <p className="text-xs mt-2"><b className="text-[var(--brand-ink)]">+</b> {pm.went_well}</p>}
                    {pm.went_poorly && <p className="text-xs mt-1"><b className="text-[var(--brand-primary)]">−</b> {pm.went_poorly}</p>}
                    {pm.lessons && <p className="text-xs mt-1 text-[var(--brand-muted)]">→ {pm.lessons}</p>}
                    <div className="mt-2 flex gap-2 text-xs">
                      <button onClick={() => setPmForm(pm)} data-testid={`pm-edit-${pm.id}`}
                        className="underline">edit</button>
                      <button onClick={() => deletePm(pm.id)} data-testid={`pm-del-${pm.id}`}
                        className="underline text-[var(--brand-primary)]">delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {pmForm ? (
              <div className="card-hard rounded-md p-4 bg-[var(--pastel-mint)] space-y-3" data-testid="pm-form">
                <div>
                  <label className="label-tiny block mb-1.5">Stage</label>
                  <select value={pmForm.stage} onChange={(e) => setPmForm({...pmForm, stage: e.target.value})}
                    data-testid="pm-stage"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white">
                    {["Application","Recruiter screen","Take-home / assessment","Technical interview","Behavioural interview","Panel / final round","Offer call"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">Date</label>
                  <input type="date" value={pmForm.interview_date || ""} onChange={(e) => setPmForm({...pmForm, interview_date: e.target.value})}
                    data-testid="pm-date"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
                {[["rating","Overall stage rating"],["interviewer_vibe","Interviewer vibe"],["process_speed","Process speed"]].map(([k, label]) => (
                  <div key={k}>
                    <label className="label-tiny block mb-1.5">{label}: {pmForm[k]}/5</label>
                    <input type="range" min={1} max={5} value={pmForm[k] || 3}
                      data-testid={`pm-${k}`}
                      onChange={(e) => setPmForm({...pmForm, [k]: Number(e.target.value)})}
                      className="w-full"/>
                  </div>
                ))}
                <div>
                  <label className="label-tiny block mb-1.5">What went well</label>
                  <textarea data-testid="pm-went-well" value={pmForm.went_well || ""} onChange={(e) => setPmForm({...pmForm, went_well: e.target.value})}
                    className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[60px]"/>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">What went poorly</label>
                  <textarea data-testid="pm-went-poorly" value={pmForm.went_poorly || ""} onChange={(e) => setPmForm({...pmForm, went_poorly: e.target.value})}
                    className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[60px]"/>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">Lessons for next time</label>
                  <textarea data-testid="pm-lessons" value={pmForm.lessons || ""} onChange={(e) => setPmForm({...pmForm, lessons: e.target.value})}
                    className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[60px]"/>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!pmForm.would_recommend_company}
                    data-testid="pm-recommend"
                    onChange={(e) => setPmForm({...pmForm, would_recommend_company: e.target.checked})}/>
                  <span>I&apos;d recommend this company to a friend</span>
                </label>
                <div className="flex gap-2 pt-2">
                  <button onClick={savePm} data-testid="pm-save"
                    className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm">save</button>
                  <button onClick={() => setPmForm(null)} data-testid="pm-cancel"
                    className="btn-hard bg-white rounded-md px-4 py-2 text-sm">cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setPmForm(EMPTY_PM)} data-testid="pm-add"
                className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm w-full flex items-center justify-center gap-2">
                <Plus size={16} weight="bold"/> add stage reflection
              </button>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
